import React from 'react';
import { Row, Col, Card, Statistic, Typography, Table, Tag, Progress, Alert } from 'antd';
import { 
  TruckOutlined, 
  UserOutlined, 
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

interface DashboardStats {
  active_vehicles: number;
  active_drivers: number;
  total_missions: number;
  pending_missions: number;
  in_progress_missions: number;
  completed_missions: number;
  missions_this_month: number;
}

interface RecentMission {
  id: string;
  title: string;
  status: 'pending' | 'assigned' | 'in_progress' | 'completed' | 'cancelled';
  priority: number;
  created_at: string;
  mission_type_name: string;
  driver_first_name?: string;
  driver_last_name?: string;
}

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();

  // Récupération des données du dashboard
  const { data: dashboardData, isLoading, error } = useQuery({
    queryKey: ['admin-dashboard'],
    queryFn: async () => {
      const response = await apiClient.get('/admin/dashboard');
      return response.data;
    },
    refetchInterval: 30000, // Rafraîchir toutes les 30 secondes
  });

  const stats: DashboardStats = dashboardData?.stats || {};
  const recentMissions: RecentMission[] = dashboardData?.recent_missions || [];

  // Calculs dérivés
  const completionRate = stats.total_missions > 0 
    ? Math.round((stats.completed_missions / stats.total_missions) * 100) 
    : 0;

  const activeTasksRate = stats.total_missions > 0
    ? Math.round(((stats.pending_missions + stats.in_progress_missions) / stats.total_missions) * 100)
    : 0;

  // Colonnes pour le tableau des missions
  const missionColumns = [
    {
      title: 'Mission',
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: RecentMission) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.mission_type_name}</div>
        </div>
      ),
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const statusConfig = {
          pending: { color: 'orange', icon: <ClockCircleOutlined />, text: 'En attente' },
          assigned: { color: 'blue', icon: <UserOutlined />, text: 'Assignée' },
          in_progress: { color: 'cyan', icon: <TruckOutlined />, text: 'En cours' },
          completed: { color: 'green', icon: <CheckCircleOutlined />, text: 'Terminée' },
          cancelled: { color: 'red', icon: <ExclamationCircleOutlined />, text: 'Annulée' },
        };
        
        const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
        
        return (
          <Tag color={config.color} icon={config.icon}>
            {config.text}
          </Tag>
        );
      },
    },
    {
      title: 'Priorité',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority: number) => (
        <Tag color={priority === 1 ? 'red' : priority === 2 ? 'orange' : 'green'}>
          {priority === 1 ? 'Haute' : priority === 2 ? 'Moyenne' : 'Normale'}
        </Tag>
      ),
    },
    {
      title: 'Chauffeur',
      key: 'driver',
      render: (record: RecentMission) => (
        record.driver_first_name ? 
          `${record.driver_first_name} ${record.driver_last_name}` : 
          <Text type="secondary">Non assigné</Text>
      ),
    },
    {
      title: 'Créée le',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => (
        <div>
          <div>{dayjs(date).format('DD/MM/YYYY')}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {dayjs(date).format('HH:mm')}
          </Text>
        </div>
      ),
    },
  ];

  if (error) {
    return (
      <div style={{ padding: '24px' }}>
        <Alert
          message="Erreur de chargement"
          description="Impossible de charger les données du tableau de bord"
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>Tableau de bord</Title>
        <Text type="secondary">
          Bienvenue {user?.first_name}, voici un aperçu de votre flotte
        </Text>
      </div>

      {/* Statistiques principales */}
      <Row gutter={[16, 16]} style={{ marginBottom: '32px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Véhicules Actifs"
              value={stats.active_vehicles || 0}
              prefix={<TruckOutlined />}
              loading={isLoading}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Chauffeurs Actifs"
              value={stats.active_drivers || 0}
              prefix={<UserOutlined />}
              loading={isLoading}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Missions en Cours"
              value={stats.in_progress_missions || 0}
              prefix={<FileTextOutlined />}
              loading={isLoading}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Taux de Completion"
              value={completionRate}
              suffix="%"
              prefix={<CheckCircleOutlined />}
              loading={isLoading}
              valueStyle={{ color: completionRate > 80 ? '#52c41a' : completionRate > 60 ? '#fa8c16' : '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Indicateurs de performance */}
      <Row gutter={[16, 16]} style={{ marginBottom: '32px' }}>
        <Col xs={24} lg={12}>
          <Card title="Performance de la Flotte" loading={isLoading}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <Text>Utilisation des véhicules</Text>
                <Text strong>
                  {stats.active_vehicles > 0 ? 
                    Math.round((stats.in_progress_missions / stats.active_vehicles) * 100) : 0}%
                </Text>
              </div>
              <Progress 
                percent={stats.active_vehicles > 0 ? 
                  Math.round((stats.in_progress_missions / stats.active_vehicles) * 100) : 0} 
                strokeColor="#1890ff"
              />
            </div>
            
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <Text>Charge de travail</Text>
                <Text strong>{activeTasksRate}%</Text>
              </div>
              <Progress 
                percent={activeTasksRate} 
                strokeColor="#722ed1"
              />
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Alertes et Notifications" loading={isLoading}>
            <div>
              {stats.pending_missions > 5 && (
                <Alert
                  message="Attention"
                  description={`${stats.pending_missions} missions en attente d'assignation`}
                  type="warning"
                  style={{ marginBottom: '12px' }}
                />
              )}
              
              {stats.active_vehicles === 0 && (
                <Alert
                  message="Aucun véhicule actif"
                  description="Activez des véhicules pour commencer les missions"
                  type="info"
                  style={{ marginBottom: '12px' }}
                />
              )}
              
              {stats.active_drivers === 0 && (
                <Alert
                  message="Aucun chauffeur actif"
                  description="Ajoutez des chauffeurs pour assigner les missions"
                  type="info"
                  style={{ marginBottom: '12px' }}
                />
              )}

              {stats.pending_missions === 0 && stats.in_progress_missions === 0 && (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <CheckCircleOutlined style={{ fontSize: '24px', color: '#52c41a' }} />
                  <div style={{ marginTop: '8px' }}>
                    <Text type="secondary">Aucune alerte en cours</Text>
                  </div>
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Missions récentes */}
      <Card title="Missions Récentes" loading={isLoading}>
        <Table
          columns={missionColumns}
          dataSource={recentMissions}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
          }}
          scroll={{ x: 700 }}
        />
      </Card>
    </div>
  );
};

export default AdminDashboard;